import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import QuestionChip from './QuestionChip';
import QuestionTestInput from './QuestionTestInput';

import type { CampaignQuestion } from '../CampaignQuestionsEditor/types';
import type { FormulaTestValues } from './formulaUtils';

interface QuestionsListProps {
    availableQuestions: CampaignQuestion[];
    allQuestions: CampaignQuestion[];
    usedQuestionIndices: Set<number>;
    testMode: boolean;
    testValues: FormulaTestValues;
    onQuestionClick: (questionIndex: number) => void;
    onTestValueChange: (questionIndex: number, value: string) => void;
}

/**
 * Displays a list of available questions as clickable chips.
 * In test mode, shows only questions that are used in the formula.
 *
 * @param {QuestionsListProps} props
 * @returns {JSX.Element}
 */
const QuestionsList: React.FC<QuestionsListProps> = (props) => {
    const { availableQuestions, allQuestions, usedQuestionIndices, testMode, testValues, onQuestionClick, onTestValueChange } = props;

    const displayedQuestions = useMemo(() => {
        if (!testMode) {
            return availableQuestions;
        }

        return availableQuestions.filter((question) => {
            const originalIndex = allQuestions.findIndex((q) => q === question);
            return usedQuestionIndices.has(originalIndex);
        });
    }, [testMode, availableQuestions, allQuestions, usedQuestionIndices]);

    return (
        <Box sx={{ flex: '0 0 45%', display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box>
                <Typography component="h4" sx={{ fontWeight: 500, mb: 0.25 }}>
                    {testMode
                        ? `Questions in formula (${displayedQuestions.length})`
                        : `Available questions (${availableQuestions.length})`}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', lineHeight: '1rem' }}>
                    {testMode
                        ? 'Enter test values for each question to calculate the formula result.'
                        : "You can use only numeric or yes/no questions in formulas. If a question doesn't appear in the " +
                          'list, please save it first.'}
                </Typography>
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    pr: 0.5,
                    overflowY: 'auto',
                    maxHeight: '400px',
                }}
            >
                {displayedQuestions.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        {testMode ? 'No questions used in formula' : 'No numeric or yes/no questions available'}
                    </Typography>
                ) : (
                    displayedQuestions.map((question, index) => {
                        const originalIndex = allQuestions.findIndex((q) => q === question);
                        const questionNumber = originalIndex + 1;
                        const isInUse = usedQuestionIndices.has(originalIndex);
                        const showTestInput = testMode && isInUse;

                        return (
                            <Box key={question.id || index} sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                <Box sx={{ flex: 1 }}>
                                    <QuestionChip
                                        question={question}
                                        questionNumber={questionNumber}
                                        onClick={() => onQuestionClick(originalIndex)}
                                        testMode={testMode}
                                        questionInUse={isInUse}
                                    />
                                </Box>
                                {showTestInput && (
                                    <QuestionTestInput
                                        questionType={question.type}
                                        testValue={testValues[originalIndex] ?? ''}
                                        onTestValueChange={(value) => onTestValueChange(originalIndex, value)}
                                    />
                                )}
                            </Box>
                        );
                    })
                )}
            </Box>
        </Box>
    );
};

export default QuestionsList;
